import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'
import { TenantSettingsContext } from '../../../../App'
import { formatMoney } from '../../../../helpers/currency'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { DataGrid } from '@mui/x-data-grid'
import useDocumentPreview from '../../../../components/Documents/useDocumentPreview'

const employeeName = (expense) =>
  `${expense?.employee?.userAccount?.firstName ?? ''} ${
    expense?.employee?.userAccount?.lastName ?? ''
  }`.trim() || 'Unknown employee'

const Expenses = ({ refreshKey }) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState(null)
  const [rejectionNote, setRejectionNote] = useState('')
  const [reviewError, setReviewError] = useState(null)

  const currency = (value) => formatMoney(value, orgSettings?.currency)

  const loadExpenses = () => {
    setLoading(true)
    API.get('/payroll/expenses')
      .then((res) => res.status === 200 && setExpenses(res.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadExpenses()
  }, [refreshKey])

  const updateStatus = (status) => {
    setReviewError(null)
    API.post(`/payroll/expenses/${selectedExpense._id}/status`, {
      status,
      rejectionNote,
    })
      .then((res) => {
        if (res.status === 200) {
          setSelectedExpense(null)
          setRejectionNote('')
          loadExpenses()
        } else {
          setReviewError(
            res.data?.error ??
              'Unable to update this expense. Please try again.',
          )
        }
      })
      .catch(() =>
        setReviewError('Unable to update this expense. Please try again.'),
      )
  }

  const { openPreview, previewModal } = useDocumentPreview()

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={2}>
        <Stack>
          <Typography variant="title2">Expenses</Typography>
          <Typography variant="body2" color="text.secondary">
            Review staff expense submissions.
          </Typography>
        </Stack>
        {loading && <LinearProgress />}
        {!loading && expenses.length === 0 ? (
          <Alert severity="info" variant="outlined" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600}>
              No expenses to review.
            </Typography>
            <Typography variant="body2">
              Staff can log an expense and optionally link it to a recent shift. Approved
              expenses can then be added to a pay run as their own line.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ height: 320 }}>
            <DataGrid
              rows={expenses}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              onRowClick={({ row }) => {
                setSelectedExpense(row)
                setRejectionNote(row.rejectionNote ?? '')
              }}
              columns={[
                {
                  field: 'employee',
                  headerName: 'Employee',
                  minWidth: 180,
                  flex: 1,
                  valueGetter: (value, row) => employeeName(row),
                },
                {
                  field: 'description',
                  headerName: 'Description',
                  minWidth: 190,
                  flex: 1,
                },
                {
                  field: 'category',
                  headerName: 'Category',
                  minWidth: 130,
                },
                {
                  field: 'expenseDate',
                  headerName: 'Date',
                  minWidth: 130,
                  valueGetter: (value) =>
                    value ? moment(value).format('DD MMM YYYY') : '',
                },
                {
                  field: 'amount',
                  headerName: 'Amount',
                  minWidth: 120,
                  valueGetter: (value) => currency(value),
                },
                {
                  field: 'shift',
                  headerName: 'Linked Shift',
                  minWidth: 130,
                  valueGetter: (value) => (value ? 'Yes' : 'No'),
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  minWidth: 120,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === 'Approved'
                          ? 'success'
                          : row.status === 'Rejected'
                            ? 'error'
                            : 'warning'
                      }
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  ),
                },
              ]}
            />
          </Box>
        )}
      </Stack>

      <Dialog
        open={!!selectedExpense}
        onClose={() => {
          setSelectedExpense(null)
          setReviewError(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedExpense?.description || 'Expense'}</DialogTitle>
        <DialogContent dividers>
          {selectedExpense && (
            <Stack gap={2}>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={employeeName(selectedExpense)} />
                <Chip label={selectedExpense.category} />
                <Chip label={selectedExpense.status} />
                <Chip label={currency(selectedExpense.amount)} color="warning" />
              </Stack>
              {selectedExpense.payRun?.reference && (
                <Alert severity="success" variant="outlined">
                  Included in pay run {selectedExpense.payRun.reference}.
                </Alert>
              )}
              {selectedExpense.notes && (
                <Typography variant="body2">{selectedExpense.notes}</Typography>
              )}
              {reviewError && (
                <Alert severity="error" onClose={() => setReviewError(null)}>
                  {reviewError}
                </Alert>
              )}
              <TextField
                label="Rejection note"
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              {selectedExpense.receipt?.key ? (
                <Button
                  startIcon={<VisibilityIcon />}
                  variant="outlined"
                  onClick={() => openPreview(selectedExpense.receipt)}
                >
                  View Receipt
                </Button>
              ) : (
                <Alert severity="info" variant="outlined">
                  No receipt was attached.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedExpense(null)
              setReviewError(null)
            }}
          >
            Close
          </Button>
          {selectedExpense?.status === 'Pending' && (
            <>
              <Button color="error" onClick={() => updateStatus('Rejected')}>
                Reject
              </Button>
              <Button variant="contained" onClick={() => updateStatus('Approved')}>
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      {previewModal}
    </Paper>
  )
}

export default Expenses
